import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectMembers } from "../../features/members/members-slice";
import { selectActivePaymentTypes } from "../../features/payment-types/payment-types-slice";
import { selectContracts } from "../../features/contracts/contracts-slice";
import {
  createPackPurchasePayment,
  createStandalonePayment,
} from "../../features/payments/slice-extensions/payments-slice-extension";
import { createMissingDraft } from "../../features/billing/slice-extensions/billing-slice-extension";
import { ContractType } from "../../features/contracts/models/ContractType";
import { ContractStatus } from "../../features/contracts/models/ContractStatus";
import { Contract } from "../../features/contracts/models/Contract";
import { formatMoney, minorUnitsToInputString, parseMoneyToMinorUnits } from "../../features/billing/utils/money";
import MemberSearchSelect from "./MemberSearchSelect";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

interface PaymentFormProps {
  mode: "pack" | "standalone";
  /** Preselects the contract (quick actions pass the member's contract). */
  initialContractId?: string;
  /** Preselects the member (e.g. launched from a member's page). */
  initialMemberId?: string;
  onSaved: () => void;
  onClose: () => void;
}

const describeContract = (c: Contract, t: TFunction): string => {
  if (c.type === ContractType.VisitPack) {
    return t("billing.paymentForm.packOf", { size: c.packSize ?? "?" });
  }
  if (c.type === ContractType.PerVisitAccount) {
    return t("billing.contractType.PerVisitAccount");
  }
  const fee = c.monthlyFeeAmountMinorUnits != null && c.monthlyFeeCurrency
    ? t("billing.common.perMonth", {
        amount: formatMoney(c.monthlyFeeAmountMinorUnits, c.monthlyFeeCurrency, getDisplayLocale()),
      })
    : t("billing.common.subscription");
  return c.type === ContractType.FlatSubscription
    ? t("billing.paymentForm.flat", { fee })
    : t("billing.paymentForm.flexible", { fee });
};

/**
 * Member-first payment creation. The chosen contract decides the payment shape:
 * a visit pack → pack purchase; a subscription → the missing month's draft;
 * "no contract" → a standalone drop-in. With exactly one active contract (other than a
 * per-visit account, which opens on drop-in) it's
 * linked automatically (still unselectable); with several, the instructor picks.
 */
// `mode` is kept for launcher compatibility; the chosen contract decides the shape.
const PaymentForm: React.FC<PaymentFormProps> = ({
  initialContractId, initialMemberId, onSaved, onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const members = useAppSelector(selectMembers);
  const paymentTypes = useAppSelector(selectActivePaymentTypes);
  const contracts = useAppSelector(selectContracts);

  const initialContract = contracts.find((c) => c.id === initialContractId);
  const [memberId, setMemberId] = useState(initialMemberId ?? initialContract?.memberId ?? "");
  const [contractId, setContractId] = useState(initialContractId ?? "");
  const [contractTouched, setContractTouched] = useState(Boolean(initialContractId));
  const [paymentTypeId, setPaymentTypeId] = useState("");
  const [packSize, setPackSize] = useState("10");
  const [amount, setAmount] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const memberContracts = useMemo(
    () => contracts.filter((c) => c.memberId === memberId && c.status === ContractStatus.Active),
    [contracts, memberId]
  );
  const selectedContract = contracts.find((c) => c.id === contractId);
  const isPack = selectedContract?.type === ContractType.VisitPack;
  const isSubscription = selectedContract != null && !isPack;

  // Auto-link: exactly one active contract → preselect it (still unselectable for a
  // drop-in); several → the instructor must pick explicitly (defaults to none). A per-visit
  // account is never preselected: its monthly lines come from the month-end draft, so a
  // hand-entered payment for that member is a one-off (e.g. Grundbetrag) — open on drop-in.
  useEffect(() => {
    if (contractTouched) return;
    const only = memberContracts.length === 1 ? memberContracts[0] : undefined;
    setContractId(only && only.type !== ContractType.PerVisitAccount ? only.id : "");
  }, [memberId, memberContracts, contractTouched]);

  // Suggest the amount from the chosen price (× pack size for packs); stays editable.
  useEffect(() => {
    const pt = paymentTypes.find((p) => p.id === paymentTypeId);
    if (!pt) return;
    const units = isPack ? Number(packSize) || 0 : 1;
    setAmount(minorUnitsToInputString(pt.priceAmountMinorUnits * units, pt.currency));
  }, [paymentTypeId, packSize, isPack, paymentTypes]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (!memberId) {
        setError(t("billing.paymentForm.errorPickMember"));
        return;
      }
      if (isSubscription) {
        await dispatch(createMissingDraft(contractId)).unwrap();
        onSaved();
        return;
      }
      const minorUnits = parseMoneyToMinorUnits(amount,
        paymentTypes.find((p) => p.id === paymentTypeId)?.currency ?? "");
      if (Number.isNaN(minorUnits) || !paymentTypeId) {
        setError(t("billing.paymentForm.errorTypeAmount"));
        return;
      }
      if (isPack) {
        if (!Number(packSize)) {
          setError(t("billing.paymentForm.errorPackSize"));
          return;
        }
        await dispatch(createPackPurchasePayment({
          contractId,
          paymentTypeId,
          packSize: Number(packSize),
          amountMinorUnits: minorUnits,
          dueDate: dueDate || undefined,
          notes: notes || undefined,
        })).unwrap();
      } else {
        await dispatch(createStandalonePayment({
          memberId,
          paymentTypeId,
          amountMinorUnits: minorUnits,
          dueDate: dueDate || undefined,
          notes: notes || undefined,
        })).unwrap();
      }
      onSaved();
    } catch (err) {
      setError((err as { message?: string })?.message ?? t("billing.paymentForm.errorCreate"));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 lg:p-6">
      <h3>{t("billing.actions.newPayment")}</h3>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MemberSearchSelect
          members={members}
          value={memberId}
          onChange={(id) => { setMemberId(id); setContractTouched(false); }}
        />

        <select
          className="input"
          value={contractId}
          onChange={(e) => { setContractId(e.target.value); setContractTouched(true); }}
          disabled={!memberId}
        >
          <option value="">{t("billing.paymentForm.noContractDropIn")}</option>
          {memberContracts.map((c) => (
            <option key={c.id} value={c.id}>{describeContract(c, t)}</option>
          ))}
        </select>

        {isSubscription ? (
          <p className="text-sm text-grey-30 dark:text-grey-70 sm:col-span-2">
            {selectedContract?.type === ContractType.PerVisitAccount
              ? t("billing.paymentForm.perVisitDraftHint")
              : t("billing.paymentForm.subscriptionDraftHint")}
          </p>
        ) : (
          <>
            {isPack && (
              <input className="input" placeholder={t("billing.contractForm.packSizePlaceholder")} inputMode="numeric"
                value={packSize} onChange={(e) => setPackSize(e.target.value)} />
            )}
            <select className="input" value={paymentTypeId} onChange={(e) => setPaymentTypeId(e.target.value)}>
              <option value="">{t("billing.paymentForm.paymentTypePlaceholder")}</option>
              {paymentTypes.map((pt) => (
                <option key={pt.id} value={pt.id}>{pt.name}</option>
              ))}
            </select>
            <input className="input" placeholder={t("billing.paymentForm.amountPlaceholder")} inputMode="decimal"
              value={amount} onChange={(e) => setAmount(e.target.value)} />
            <label className="text-sm text-grey-30 dark:text-grey-70">
              {t("billing.paymentForm.dueDateOptional")}
              <input type="date" className="input mt-1" value={dueDate}
                onChange={(e) => setDueDate(e.target.value)} />
            </label>
            <input className="input sm:col-span-2" placeholder={t("billing.common.notesOptionalPlaceholder")}
              value={notes} onChange={(e) => setNotes(e.target.value)} />
          </>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
      <div className="mt-4 flex justify-end gap-3">
        <button type="button" className="btn btn-secondary" onClick={onClose}>{t("billing.common.cancel")}</button>
        <button type="submit" className="btn btn-primary">
          {isSubscription ? t("billing.actions.createDraft") : t("billing.paymentForm.create")}
        </button>
      </div>
    </form>
  );
};

export default PaymentForm;
